import { Box3, Vector3, Vector3Tuple } from 'three';

// caching vectors in order to avoid too much garbage collection
const _vectorsCache: { [xyz: string]: Vector3 } = {};
const _box3Cache: { [xyzxyz: string]: Box3 } = {};

export function vector3(xyz: Vector3Tuple) {
  const key = vectorKey(xyz);
  if (!_vectorsCache[key]) {
    _vectorsCache[key] = new Vector3(...xyz);
  }
  return _vectorsCache[key];
}

export function equalsVector3(xyz: Vector3Tuple, xyz2: Vector3Tuple) {
  for (let i = 0; i < 3; i++) {
    if (xyz[i] !== xyz2[i]) {
      return false;
    }
  }

  return true;
}

export function vector3tuple(v: Vector3) {
  return v.toArray();
}

function vectorKey(xyz: Vector3Tuple) {
  return xyz.join(',');
}

function box3Key(min: Vector3Tuple, max: Vector3Tuple) {
  return `${vectorKey(min)}#${vectorKey(max)}`;
}

export function box3([min, max]: [Vector3Tuple, Vector3Tuple]) {
  const key = box3Key(min, max);
  if (!_box3Cache[key]) {
    _box3Cache[key] = new Box3().setFromPoints([vector3(min), vector3(max)]);
  }

  return _box3Cache[key];
}

export function box3tuple(box3: Box3): [Vector3Tuple, Vector3Tuple] {
  return [vector3tuple(box3.min), vector3tuple(box3.max)];
}

export function translateBox3(
  [min, max]: [Vector3Tuple, Vector3Tuple],
  offset: Vector3Tuple,
) {
  const key = `${box3Key(min, max)}t${vectorKey(offset)}`;

  if (!_box3Cache[key]) {
    _box3Cache[key] = new Box3()
      .setFromPoints([vector3(min), vector3(max)])
      .translate(vector3(offset));
  }

  return _box3Cache[key];
}

export function includeBox3(bboxes: [Vector3Tuple, Vector3Tuple][]) {
  const key = `${bboxes.map(([min, max]) => box3Key(min, max)).join(`i`)}`;

  if (!bboxes.length) {
    throw new Error('Invalid argument bboxes empty');
  }

  if (!_box3Cache[key]) {
    const result = new Box3().setFromPoints([
      vector3(bboxes[0][0]),
      vector3(bboxes[0][1]),
    ]);

    for (const [min, max] of bboxes.slice(1)) {
      result.expandByPoint(vector3(min));
      result.expandByPoint(vector3(max));
    }

    _box3Cache[key] = result;
  }

  return _box3Cache[key];
}

export function translateVector3(vector: Vector3Tuple, offset: Vector3Tuple) {
  const key = `${vectorKey(vector)}t${vectorKey(offset)}`;

  if (!_vectorsCache[key]) {
    _vectorsCache[key] = new Vector3(...vector).add(vector3(offset));
  }

  return _vectorsCache[key];
}
